import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import AuthorBox from "../components/AuthorBox";
import ContactModal from "../components/ContactModal";
import SEOHead from "../components/SEOHead";
import AISearchOptimization from "../components/AISearchOptimization";
import { Button } from "../components/ui/button";
import {
  Calendar,
  CheckCircle,
  ArrowRight,
  Zap,
  Users,
  Target,
  TrendingUp,
  Shield,
  Lightbulb,
  Settings,
  BarChart3,
  Brain,
  Building2,
  Layers,
  GraduationCap,
  Briefcase,
  Globe,
  Database,
  Network,
  Rocket,
  Award,
  Search
} from "lucide-react";

const AIDrivenEnterprise = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const seoConfig = {
    title: "AI Driven Enterprise — Vom KI-Experiment zur KI-getriebenen Organisation | BROVKO",
    description: "BROVKO begleitet Unternehmen auf dem Weg zum AI Driven Enterprise: KI-Strategie, organisatorische Transformation und technologische Umsetzung aus einer Hand.",
    keywords: "AI Driven Enterprise, KI-Transformation, KI-Strategie, AI Value Engine, AI Enterprise Model, AI Transformation Framework, BROVKO",
    canonicalUrl: "/ai-driven-enterprise",
    ogTitle: "AI Driven Enterprise — Vom KI-Experiment zur KI-getriebenen Organisation",
    ogDescription: "Die meisten Unternehmen experimentieren mit KI. Nur wenige transformieren ihre Organisation. BROVKO ist der Architekt des AI Driven Enterprise.",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "BROVKO AI Driven Enterprise Program",
      "description": "Strategische KI-Transformation: Vom Pilotprojekt zum AI Driven Enterprise mit dem BROVKO 4+1 System.",
      "provider": {
        "@type": "Organization",
        "name": "BROVKO"
      },
      "areaServed": ["DE", "AT", "CH"],
      "serviceType": "AI Transformation Consulting",
      "offers": {
        "@type": "Offer",
        "description": "AI Driven Enterprise Program — Strategische Beratung, organisatorische Transformation, technologische Umsetzung",
        "areaServed": ["Germany", "Austria", "Switzerland"]
      }
    }
  };

  const openContactModal = () => {
    setIsContactModalOpen(true);
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
  };

  // Challenge items
  const challenges = [
    {
      icon: Target,
      title: "Fehlende KI-Strategie",
      description: "Kein klares Zielbild, wie KI das Geschäftsmodell transformieren soll."
    },
    {
      icon: Layers,
      title: "Fragmentierte Initiativen",
      description: "Isolierte Pilotprojekte ohne unternehmensweite Koordination."
    },
    {
      icon: Settings,
      title: "Keine Prozessintegration",
      description: "KI-Lösungen werden nicht in Kernprozesse und Entscheidungen integriert."
    },
    {
      icon: Users,
      title: "Fehlende Fähigkeiten",
      description: "Organisatorische Kompetenzen für systematischen KI-Einsatz fehlen."
    }
  ];

  // DNA elements of an AI Driven Enterprise
  const dnaElements = [
    {
      icon: Zap,
      title: "Speed",
      description: "Schnellere Entscheidungen, kürzere Innovationszyklen, beschleunigte Time-to-Market."
    },
    {
      icon: BarChart3,
      title: "Data-Driven Decisions",
      description: "Datenbasierte Entscheidungsfindung als dominantes Management-Paradigma."
    },
    {
      icon: Network,
      title: "Process Orientation",
      description: "Prozessorientierte Organisationen überholen funktional organisierte Unternehmen."
    },
    {
      icon: Brain,
      title: "AI-Empowered Knowledge Workers",
      description: "Wissensarbeiter, die KI als strategisches Werkzeug für Produktivität und Innovation nutzen."
    }
  ];

  // Comparison table
  const comparisonRows = [
    { aspect: "KI-Strategie", traditional: "Keine oder punktuell", aiDriven: "Unternehmensweites Zielbild" },
    { aspect: "KI-Initiativen", traditional: "Isolierte Pilotprojekte", aiDriven: "Systematische Skalierung" },
    { aspect: "Entscheidungen", traditional: "Erfahrungsbasiert", aiDriven: "Datengetrieben" },
    { aspect: "Wissensarbeit", traditional: "Manuelle Prozesse", aiDriven: "AI-Empowered" },
    { aspect: "Innovation", traditional: "Langsame Zyklen", aiDriven: "Beschleunigte Experimente" },
    { aspect: "Organisation", traditional: "Funktionale Silos", aiDriven: "Prozessorientiert & vernetzt" }
  ];

  // BROVKO System (4+1)
  const systemLayers = [
    {
      number: "1",
      icon: Target,
      title: "AI Driven Enterprise Target Picture",
      subtitle: "Zielbild entwickeln",
      description: "Die DNA des AI Driven Enterprise definieren: Speed, Data-Driven Decisions, Process Orientation, AI-Empowered Knowledge Workers.",
      color: "teal"
    },
    {
      number: "2",
      icon: TrendingUp,
      title: "AI Value Engine",
      subtitle: "Wertschöpfung systematisieren",
      description: "Vier Werthebel identifizieren und aktivieren: Revenue Growth, Operational Efficiency, Innovation Acceleration, Risk Reduction.",
      color: "teal"
    },
    {
      number: "3",
      icon: Building2,
      title: "AI Enterprise Model",
      subtitle: "Organisation transformieren",
      description: "AI Governance, cross-funktionale AI Teams, AI Champions, AI Black Belts und eine AI Academy etablieren.",
      color: "teal"
    },
    {
      number: "4",
      icon: Database,
      title: "AI Enterprise Architecture",
      subtitle: "Technologie aufbauen",
      description: "Data Platform, AI Platform, Generative AI Systems, Knowledge Graph, AI Agents — die technologische Basis für Skalierung.",
      color: "teal"
    },
    {
      number: "+1",
      icon: Rocket,
      title: "AI Transformation Framework",
      subtitle: "Transformation orchestrieren",
      description: "Strukturierter Transformationsprozess in 4 Phasen: von der Readiness bis zur unternehmensweiten Skalierung.",
      color: "teal"
    }
  ];

  // Transformation phases
  const transformationPhases = [
    {
      phase: "Phase 1",
      icon: Search,
      title: "AI Readiness & Opportunity Assessment",
      description: "Aktuellen KI-Reifegrad bewerten und High-Value Use Cases identifizieren.",
      outcomes: [
        "AI Readiness Assessment",
        "AI Opportunity Map",
        "Priorisierte AI Use Cases"
      ]
    },
    {
      phase: "Phase 2",
      icon: Target,
      title: "AI Target Model",
      description: "Zukünftige KI-Strategie und Operating Model definieren.",
      outcomes: [
        "AI-Strategie",
        "AI Governance Model",
        "AI Enterprise Architecture"
      ]
    },
    {
      phase: "Phase 3",
      icon: Settings,
      title: "AI Value Engine Development",
      description: "KI-Anwendungen mit messbarem Business Impact entwickeln und implementieren.",
      outcomes: [
        "AI-driven Sales & Marketing",
        "AI-gestützte Entscheidungen",
        "AI-basierte Prozessoptimierung"
      ]
    },
    {
      phase: "Phase 4",
      icon: Rocket,
      title: "Scaling AI Across the Enterprise",
      description: "Erfolgreiche Anwendungen skalieren und organisatorische Fähigkeiten aufbauen.",
      outcomes: [
        "Enterprise-weite AI Teams",
        "AI Champions Netzwerk",
        "Unternehmensweite AI Platform"
      ]
    }
  ];

  // Capability Development
  const capabilities = [
    {
      icon: Award,
      title: "AI Champions",
      description: "Mitarbeiter, die neue KI-Chancen identifizieren und ihre Teams bei der Einführung von KI-Tools unterstützen.",
      details: [
        "KI-Potenziale im eigenen Bereich erkennen",
        "Teams bei der KI-Nutzung unterstützen",
        "Brücke zwischen Fachbereich und IT"
      ]
    },
    {
      icon: Shield,
      title: "AI Black Belts",
      description: "Fortgeschrittene Praktiker, die KI-Initiativen über die gesamte Organisation hinweg leiten.",
      details: [
        "High-Value Use Cases identifizieren",
        "Implementierungsprojekte leiten",
        "Teams zur effektiven KI-Nutzung befähigen"
      ]
    },
    {
      icon: GraduationCap,
      title: "AI Academy",
      description: "Strukturierte Lernprogramme — vom Executive Briefing bis zur unternehmensweiten Qualifizierung.",
      details: [
        "AI Executive Briefings",
        "AI Leadership Workshops",
        "Projektbasiertes Lernen (Transformation on the Job)"
      ]
    }
  ];

  // Collaboration models
  const collaborationModels = [
    {
      icon: Lightbulb,
      title: "Strategic Advisory",
      description: "KI-Strategie, Governance und Transformations-Roadmaps definieren."
    },
    {
      icon: Users,
      title: "Executive Coaching",
      description: "Führungsteams befähigen, KI strategisch zu verstehen und einzusetzen."
    },
    {
      icon: Briefcase,
      title: "Transformation on the Job",
      description: "Kompetenzen aufbauen, während an realen KI-Initiativen gearbeitet wird."
    },
    {
      icon: Settings,
      title: "Implementation & Development",
      description: "KI-Anwendungen und Datenplattformen implementieren und skalieren."
    }
  ];

  // Target audience
  const targetAudience = [
    "CEOs und Geschäftsführer",
    "CIOs und CTOs",
    "Chief Digital Officers",
    "Strategie- und Transformationsleiter"
  ];

  return (
    <div className="theme-brand bg-surface text-ink min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      <SEOHead {...seoConfig} />
      <Header />

      {/* Hero Section */}
      <section className="pt-40 pb-10">
        <div className="container mx-auto px-4">
          <div className="max-w-[46rem]">
            <div className="inline-flex items-center gap-2 bg-teal-400/10 border border-teal-400/30 rounded-full px-4 py-1.5 mb-6">
              <Brain className="h-4 w-4 text-brand-400" />
              <span className="text-brand-400 text-xs font-medium">BROVKO AI Driven Enterprise Program</span>
            </div>
            <h1 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-4 leading-tight">
              From AI Experiments to<br />
              <span className="text-brand-400">AI Driven Enterprises</span>
            </h1>
            <p className="text-base text-ink-muted mb-4 leading-relaxed max-w-[62ch]">
              Die meisten Unternehmen experimentieren mit KI.
              Nur wenige transformieren ihre Organisation.
            </p>
            <p className="text-sm text-ink-faint mb-6 max-w-[62ch]">
              BROVKO begleitet Unternehmen dabei, Künstliche Intelligenz systematisch in
              Strategie, Geschäftsprozesse, Entscheidungsstrukturen und Organisation zu integrieren.
            </p>
            <Button
              className="bg-teal-400 hover:bg-brand text-ink-onBrand px-6 py-3 text-sm font-semibold"
              onClick={openContactModal}
            >
              <Calendar className="mr-2 h-4 w-4" />
              Strategisches Erstgespräch vereinbaren
            </Button>
          </div>
        </div>
      </section>

      {/* Challenge Section */}
      <section className="py-20 md:py-28 bg-surface-raised/50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-3 text-center">
              Die Pilotprojektfalle
            </h2>
            <p className="text-base text-ink-faint mb-8 text-center max-w-[62ch]">
              Viele Unternehmen starten KI-Piloten — Chatbots, Analytik-Tools, Automatisierung.
              Doch die meisten Initiativen bleiben isoliert und verändern nicht, wie die Organisation arbeitet.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {challenges.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div key={index} className="bg-surface-raised/50 backdrop-blur-sm rounded-xl p-5 border border-hairline hover:border-red-400/30 transition-colors">
                    <div className="bg-red-400/10 p-2 rounded-lg w-fit mb-3">
                      <IconComponent className="h-5 w-5 text-red-400" />
                    </div>
                    <h3 className="text-sm font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-ink-faint text-xs leading-relaxed">{item.description}</p>
                  </div>
                );
              })}
            </div>
            <p className="text-sm text-ink-muted mt-6 text-center">
              Um das volle Potenzial von KI zu erschließen, müssen Unternehmen über isolierte Anwendungen hinausgehen —
              sie müssen sich zu <span className="text-brand-400 font-semibold">AI Driven Enterprises</span> entwickeln.
            </p>
          </div>
        </div>
      </section>

      {/* What is an AI Driven Enterprise */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-3 text-center">
              Was ist ein AI Driven Enterprise?
            </h2>
            <p className="text-base text-ink-faint mb-8 text-center max-w-[62ch]">
              Ein Unternehmen, das Künstliche Intelligenz systematisch in Prozesse,
              Entscheidungen, Wissensarbeit und Innovation integriert.
            </p>

            {/* DNA Elements */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {dnaElements.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div key={index} className="bg-surface-raised/50 backdrop-blur-sm rounded-xl p-5 border border-hairline hover:border-teal-400/50 transition-colors">
                    <div className="bg-teal-400/10 p-2 rounded-lg w-fit mb-3">
                      <IconComponent className="h-5 w-5 text-brand-400" />
                    </div>
                    <h3 className="text-sm font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-ink-faint text-xs leading-relaxed">{item.description}</p>
                  </div>
                );
              })}
            </div>

            {/* Comparison Table */}
            <div className="bg-surface-raised/50 backdrop-blur-sm rounded-xl border border-hairline overflow-hidden">
              <div className="grid grid-cols-3 bg-white/[0.05]">
                <div className="p-3 text-xs font-semibold text-ink-faint uppercase tracking-wider"></div>
                <div className="p-3 text-xs font-semibold text-ink-faint uppercase tracking-wider text-center">Traditional Enterprise</div>
                <div className="p-3 text-xs font-semibold text-brand-400 uppercase tracking-wider text-center">AI Driven Enterprise</div>
              </div>
              {comparisonRows.map((row, index) => (
                <div key={index} className={`grid grid-cols-3 ${index % 2 === 0 ? 'bg-slate-800/30' : ''}`}>
                  <div className="p-3 text-xs font-medium text-white">{row.aspect}</div>
                  <div className="p-3 text-xs text-ink-faint text-center">{row.traditional}</div>
                  <div className="p-3 text-xs text-brand-400 text-center font-medium">{row.aiDriven}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BROVKO System (4+1) */}
      <section className="py-20 md:py-28 bg-slate-800/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-3 text-center">
              Das BROVKO System
            </h2>
            <p className="text-base text-ink-faint mb-8 text-center max-w-[62ch]">
              4+1 Ebenen für den systematischen Aufbau eines AI Driven Enterprise —
              von der strategischen Vision bis zur operativen Umsetzung.
            </p>
            <div className="space-y-4">
              {systemLayers.map((layer, index) => {
                const IconComponent = layer.icon;
                return (
                  <div key={index} className="bg-surface-raised/50 backdrop-blur-sm rounded-xl p-5 border border-hairline hover:border-teal-400/50 transition-colors">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 bg-teal-400/10 rounded-lg p-3 flex items-center justify-center">
                        <span className="text-brand-400 font-bold text-lg min-w-[2rem] text-center">{layer.number}</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <IconComponent className="h-4 w-4 text-brand-400" />
                          <h3 className="text-base font-bold text-white">{layer.title}</h3>
                        </div>
                        <p className="text-brand-400 text-xs font-medium mb-2">{layer.subtitle}</p>
                        <p className="text-ink-muted text-sm leading-relaxed">{layer.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Transformation Framework */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-3 text-center">
              AI Transformation Framework
            </h2>
            <p className="text-base text-ink-faint mb-8 text-center max-w-[62ch]">
              Die Transformation zum AI Driven Enterprise folgt vier strukturierten Phasen.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {transformationPhases.map((phase, index) => {
                const IconComponent = phase.icon;
                return (
                  <div key={index} className="bg-surface-raised/50 backdrop-blur-sm rounded-xl p-5 border border-hairline hover:border-teal-400/50 transition-colors">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-teal-400/10 p-2 rounded-lg">
                        <IconComponent className="h-5 w-5 text-brand-400" />
                      </div>
                      <div>
                        <span className="text-brand-400 text-xs font-medium">{phase.phase}</span>
                        <h3 className="text-base font-bold text-white">{phase.title}</h3>
                      </div>
                    </div>
                    <p className="text-ink-muted text-sm mb-4 leading-relaxed">{phase.description}</p>
                    <ul className="space-y-2">
                      {phase.outcomes.map((outcome, oIndex) => (
                        <li key={oIndex} className="flex items-start gap-2">
                          <CheckCircle className="h-3 w-3 text-brand-400 mt-0.5 flex-shrink-0" />
                          <span className="text-ink-muted text-xs leading-relaxed">{outcome}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Capability Development */}
      <section className="py-20 md:py-28 bg-surface-raised/50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-3 text-center">
              Capability Development
            </h2>
            <p className="text-base text-ink-faint mb-8 text-center max-w-[62ch]">
              Erfolgreiche KI-Transformationen brauchen interne Multiplikatoren.
              Das BROVKO-Programm entwickelt die Menschen, die den Wandel tragen.
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              {capabilities.map((cap, index) => {
                const IconComponent = cap.icon;
                return (
                  <div key={index} className="bg-surface-raised/50 backdrop-blur-sm rounded-xl p-5 border border-hairline hover:border-teal-400/50 transition-colors">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-teal-400/10 p-2 rounded-lg">
                        <IconComponent className="h-5 w-5 text-brand-400" />
                      </div>
                      <h3 className="text-base font-bold text-white">{cap.title}</h3>
                    </div>
                    <p className="text-ink-muted text-sm mb-4 leading-relaxed">{cap.description}</p>
                    <ul className="space-y-2">
                      {cap.details.map((detail, dIndex) => (
                        <li key={dIndex} className="flex items-start gap-2">
                          <CheckCircle className="h-3 w-3 text-brand-400 mt-0.5 flex-shrink-0" />
                          <span className="text-ink-muted text-xs leading-relaxed">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Collaboration Models */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-3 text-center">
              Zusammenarbeit
            </h2>
            <p className="text-base text-ink-faint mb-8 text-center max-w-[62ch]">
              Flexible Zusammenarbeitsmodelle — von der strategischen Beratung bis zur operativen Umsetzung.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {collaborationModels.map((model, index) => {
                const IconComponent = model.icon;
                return (
                  <div key={index} className="bg-surface-raised/50 backdrop-blur-sm rounded-xl p-5 border border-hairline hover:border-teal-400/50 transition-colors text-center">
                    <div className="bg-teal-400/10 p-3 rounded-lg w-fit mx-auto mb-3">
                      <IconComponent className="h-5 w-5 text-brand-400" />
                    </div>
                    <h3 className="text-sm font-bold text-white mb-2">{model.title}</h3>
                    <p className="text-ink-faint text-xs leading-relaxed">{model.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* AI Case Hub */}
      <section className="py-20 md:py-28 bg-slate-800/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-surface-raised/50 backdrop-blur-sm rounded-xl p-6 border border-hairline">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="bg-teal-400/10 p-4 rounded-xl flex-shrink-0">
                  <Globe className="h-8 w-8 text-brand-400" />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-2">AI Case Hub</h2>
                  <p className="text-ink-muted text-sm leading-relaxed mb-3">
                    BROVKO unterhält einen AI Case Hub mit über 600 internationalen Praxisfällen.
                    Dieser ermöglicht es, frühzeitig zu erkennen, wie sich Unternehmen und Branchen
                    durch KI weiterentwickeln — und welche Ansätze tatsächlich funktionieren.
                  </p>
                  <a
                    href="https://aicasehub.io"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-brand-400 hover:text-brand-300 text-sm font-medium transition-colors"
                  >
                    aicasehub.io besuchen
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-3">
              Für wen?
            </h2>
            <p className="text-base text-ink-faint mb-6">
              Das AI Driven Enterprise Program richtet sich an Entscheider, die KI strategisch verankern wollen.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {targetAudience.map((role, index) => (
                <div key={index} className="flex items-center gap-2 bg-surface-raised/50 border border-hairline rounded-lg px-4 py-2">
                  <CheckCircle className="h-4 w-4 text-brand-400 flex-shrink-0" />
                  <span className="text-white text-sm font-medium">{role}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-surface-raised/50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-4">
              Starten Sie Ihre AI Transformation
            </h2>
            <p className="text-base text-ink-muted mb-3 max-w-[62ch]">
              Künstliche Intelligenz wird verändern, wie Unternehmen arbeiten.
              Die Frage ist nicht mehr ob, sondern wie systematisch Sie KI in Ihr
              Geschäftsmodell und Ihre Entscheidungsprozesse integrieren.
            </p>
            <p className="text-sm text-ink-faint mb-6 max-w-[62ch]">
              Unternehmen, die sich erfolgreich zu AI Driven Enterprises transformieren,
              werden das kommende Jahrzehnt prägen.
            </p>
            <div className="flex justify-center">
              <Button
                className="bg-teal-400 hover:bg-brand text-ink-onBrand px-6 py-3 text-sm font-semibold"
                onClick={openContactModal}
              >
                <Calendar className="mr-2 h-4 w-4" />
                Strategisches Erstgespräch vereinbaren
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4">
        <AuthorBox />
      </div>

      <Footer />

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={closeContactModal}
        pageSource="AI Driven Enterprise"
        buttonContext="AI Driven Enterprise Program"
        buttonText="Strategisches Erstgespräch vereinbaren"
      />

      <AISearchOptimization
        pageType="service"
        title="AI Driven Enterprise — Vom KI-Experiment zur KI-getriebenen Organisation"
        description="BROVKO begleitet Unternehmen auf dem Weg zum AI Driven Enterprise: KI-Strategie, organisatorische Transformation und technologische Umsetzung mit dem 4+1 System."
        keywords={[
          'AI Driven Enterprise',
          'KI-Transformation Unternehmen',
          'AI Value Engine',
          'AI Enterprise Model',
          'AI Transformation Framework',
          'KI-Strategie',
          'AI Champions',
          'AI Black Belts'
        ]}
        faqData={[
          {
            question: "Was ist ein AI Driven Enterprise?",
            answer: "Ein AI Driven Enterprise integriert Künstliche Intelligenz systematisch in Geschäftsprozesse, Entscheidungen, Wissensarbeit und Innovation. Die vier DNA-Elemente sind: Speed, Data-Driven Decisions, Process Orientation und AI-Empowered Knowledge Workers."
          },
          {
            question: "Was ist das BROVKO 4+1 System?",
            answer: "Das BROVKO 4+1 System umfasst: (1) AI Driven Enterprise Target Picture, (2) AI Value Engine, (3) AI Enterprise Model, (4) AI Enterprise Architecture und (+1) AI Transformation Framework — von der Vision bis zur Umsetzung."
          },
          {
            question: "Für wen ist das AI Driven Enterprise Program?",
            answer: "Das Programm richtet sich an CEOs, CIOs/CTOs, Chief Digital Officers sowie Strategie- und Transformationsleiter, die KI strategisch in ihrer Organisation verankern wollen."
          }
        ]}
      />
    </div>
  );
};

export default AIDrivenEnterprise;
